import { Link } from 'react-router-dom';
import { useHashScroll } from '../lib/useHashScroll';
import { PublicHeader } from '../components/PublicHeader';
import { SiteFooter } from '../components/SiteFooter';
import { CinematicReel } from '../components/CinematicReel';
import { CinematicBand, useReveal } from '../components/CinematicBits';
import {
  AskDemo, ArrearsBands, BeforeAfter, CountUp, CoverageExplorer, JourneyStrip, MaintenanceBoard,
  MoneyChart, PaymentDemo, Photo, PortalPhone, PropertyRings, StageSlider, StepFlow,
} from '../components/SolutionsInteractive';
import '../styles/landing.css';
import '../styles/solutions-interactive.css';
import '../styles/public.css';

// Up to five slides, sharpest first. Two are AI-generated (sol-*), the rest are
// the existing property photos, for variety on the Solutions showcase.
const SOL_SLIDES = [
  { src: '/cinematic/skyline.jpg', alt: 'A modern residential neighbourhood in Nairobi at golden hour', caption: 'Every building you look after, in one view.' },
  { src: '/images/sol-building.webp', alt: 'A modern apartment building at golden hour', caption: 'Know what needs your attention, before you go looking for it.' },
  { src: '/images/sol-estate.webp', alt: 'A modern gated estate of contemporary townhouses', caption: 'From a single building to a growing estate.' },
  { src: '/landlord-mobile.webp', alt: 'A property manager checking her portfolio on a phone', caption: 'Track rent, tenants and receipts from your phone.' },
  { src: '/hero-building.webp', alt: 'A modern apartment building in Nairobi', caption: 'Anticipate problems before they cost you money.' },
  { src: '/estate-townhouses.webp', alt: 'A row of modern townhouses in a gated estate', caption: 'One platform for your entire property business.' },
];

/*
 * The capability tour: the screens that settle "but does it actually do X".
 *
 * Deliberately NOT the homepage six. Those answer how the portfolio is doing —
 * the dashboard, rent, what is billed and owed, and the owner's own three
 * screens. These answer what the product can PRODUCE: a numbered receipt, a
 * reconciled M-Pesa payment, a meter reading ready to bill on. Somebody reads
 * the homepage to be interested and this page to be convinced, and the two want
 * different pictures.
 *
 * Nothing appears on both. When invoices moved to the homepage — the arrears
 * screen it replaced showed a demo account with nobody in arrears, so every
 * band read zero — it came off this list rather than being shown twice.
 */
const SOL_SCREENS = [
  { src: '/screens/portfolio-glance.webp?v=2', alt: 'The portfolio at a glance: collection rate, occupancy, rent outstanding and net income, with the charts behind them',
    caption: 'Portfolio at a glance: the same figures as your dashboard, with the report behind each one a click away.' },
  { src: '/screens/leases.webp?v=2', alt: 'The leases list showing lease status, tenants, units and expiry dates',
    caption: 'Leases with a lifecycle of their own, and reminders at 90, 60 and 30 days.' },
  { src: '/screens/vacancies.webp?v=2', alt: 'The vacancies screen showing empty units available to list publicly',
    caption: 'Vacancies, ready to advertise on the public marketplace in a couple of clicks.' },
  { src: '/screens/utilities.webp?v=2', alt: 'The utility readings screen showing water and electricity consumption per meter',
    caption: 'Water and electricity read per unit, period by period, ready to bill on.' },
  /*
   * The reports folder came off the homepage when the owner portal took its
   * place there. It belongs here rather than nowhere: "twenty-one reports" is a
   * claim, and this is the screen that settles it.
   */
  { src: '/screens/reports.webp?v=2', alt: 'The reports screen showing report folders for property, tenants, rent, financial, maintenance and compliance',
    caption: 'Twenty-one reports in six folders. Every one downloads as PDF or Excel.' },
];

/**
 * The Solutions page. It answers one question — "how does NyumbaSuite help me
 * manage and grow my property business?" — organised around outcomes rather than
 * a feature list, with dashboard mockups and workflow diagrams instead of walls
 * of text. It deliberately does not replicate the logged-in app; it explains and
 * sells. Anything not yet built (M-Pesa collection, WhatsApp delivery) is
 * clearly marked as planned; everything else on this page is live today.
 */

// The arc the product is being built along, and where it honestly stands on it.
// The old spine (Manage / Collect / Operate / Analyze / Grow / Connect) named
// six areas of a feature list; this names the progression from recording what
// happened to deciding what to do about it.
//
// The last two are not built. A chip row that implied otherwise would be the
// same overclaim the "Coming soon" markers elsewhere on the site exist to stop,
// and the caption underneath says so in words rather than leaving it to colour.
const SPINE: { label: string; live: boolean; text: string }[] = [
  { label: 'Manage', live: true, text: 'Properties, units, tenants, leases and payments recorded once, in one place, and always current.' },
  { label: 'Understand', live: true, text: 'Occupancy, collection rate, income and arrears shown as plain answers rather than raw tables.' },
  { label: 'Anticipate', live: true, text: 'Early signals from your own records, such as a balance that has risen three months running.' },
  { label: 'Decide', live: false, text: 'Being built: a clear view of what to do first, with the reasons beside it.' },
  { label: 'Act', live: false, text: 'Being built: carrying out the agreed action for you, with you approving it.' },
];

// The five business categories, each a grouping of things that already exist —
// except the two marked, which are the roadmap and say so.
const CATEGORIES: { name: string; items: { label: string; soon?: true }[] }[] = [
  { name: 'Financial operations', items: [
    { label: 'Rent' }, { label: 'Payments' }, { label: 'Invoices' }, { label: 'Receipts' },
    { label: 'Expenses' }, { label: 'Collections' }, { label: 'Statements' }] },
  { name: 'Property operations', items: [
    { label: 'Properties' }, { label: 'Units' }, { label: 'Tenancies' }, { label: 'Maintenance' },
    { label: 'Inspections' }, { label: 'Utilities' }] },
  { name: 'Tenant experience', items: [
    { label: 'Tenant portal' }, { label: 'Statements' }, { label: 'Maintenance requests' },
    { label: 'Documents' }, { label: 'Communications' }] },
  { name: 'Portfolio intelligence', items: [
    { label: 'Occupancy' }, { label: 'Arrears' }, { label: 'Profitability' }, { label: 'Vacancy' },
    { label: 'Performance' }, { label: 'Forecasting', soon: true }] },
  { name: 'AI & automation', items: [
    { label: 'AI assistant' }, { label: 'Rent reminders' }, { label: 'Renewal reminders' },
    { label: 'Smart alerts', soon: true }, { label: 'AI recommendations', soon: true },
    { label: 'Business process advisor', soon: true }] },
];

/** A stylised marketing snapshot of the dashboard — not the real app screen. */
function DashboardMock() {
  return (
    <div className="lp-preview" role="img" aria-label="A NyumbaSuite dashboard snapshot: 3 properties, 8 units, 75% occupancy, rent collected this period">
      <div className="lp-preview-bar" aria-hidden>
        <span className="lp-preview-dots"><i /><i /><i /></span>
        <span className="lp-preview-title">NyumbaSuite · Dashboard</span>
      </div>
      <div className="lp-preview-body" aria-hidden>
        <div className="lp-preview-kpis">
          <div className="lp-pk"><span className="lp-pk-label">Properties</span><span className="lp-pk-value"><CountUp to={3} /></span></div>
          <div className="lp-pk"><span className="lp-pk-label">Units</span><span className="lp-pk-value"><CountUp to={8} /></span><span className="lp-pk-sub">6 occupied · 2 vacant</span></div>
          <div className="lp-pk"><span className="lp-pk-label">Occupancy</span><span className="lp-pk-value"><CountUp to={75} suffix="%" /></span></div>
          <div className="lp-pk"><span className="lp-pk-label">Net income</span><span className="lp-pk-value"><CountUp to={339100} prefix="KES " /></span><span className="lp-pk-sub">Collected less expenses</span></div>
        </div>
        <div className="lp-preview-rent">
          <div className="lp-pr-head">Rent this period</div>
          <div className="lp-pr-grid">
            <div className="lp-pr"><span className="lp-pr-label">Expected</span><span className="lp-pr-value"><CountUp to={525000} prefix="KES " /></span></div>
            <div className="lp-pr"><span className="lp-pr-label">Collected</span><span className="lp-pr-value pos"><CountUp to={475500} prefix="KES " /></span></div>
            <div className="lp-pr"><span className="lp-pr-label">Outstanding</span><span className="lp-pr-value neg"><CountUp to={49500} prefix="KES " /></span></div>
            <div className="lp-pr"><span className="lp-pr-label">Collection rate</span><span className="lp-pr-value"><CountUp to={90.57} decimals={2} suffix="%" /></span></div>
          </div>
          <div className="lp-pr-meter"><span style={{ width: '90.57%' }} /></div>
        </div>
      </div>
    </div>
  );
}

export function Solutions() {
  /* Was its own copy of the same 60ms guess — see useHashScroll. */
  useHashScroll({ topWhenNoHash: true });
  useReveal();

  return (
    <div className="lp mk">
      <PublicHeader />

      {/* ---- hero ---------------------------------------------------------- */}
      <section className="mk-hero" id="landlord-pm" style={{ paddingBottom: 'var(--space-6)' }}>
        <span className="mk-eyebrow">Solutions</span>
        <h1 className="mk-title">One platform. Intelligent property operations.</h1>
        <p className="mk-lede">
          Manage your properties, tenants and finances while NyumbaSuite helps you understand
          how the portfolio is performing, spot what needs attention early, and decide what to
          do next.
        </p>
        <div className="mk-cta-row" style={{ marginTop: 'var(--space-6)' }}>
          <Link className="btn btn-primary" to="/register">Get started</Link>
          <Link className="btn btn-secondary" to="/demo">Request a demo</Link>
        </div>
      </section>

      <section className="mk-section" style={{ paddingTop: 0 }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <CinematicReel slides={SOL_SLIDES} />
        </div>
        <JourneyStrip stops={SPINE} />
        <p className="field-hint" style={{ textAlign: 'center', marginTop: 'var(--space-3)' }}>
          Manage, understand and anticipate are live today. Decide and act are being built.
        </p>
      </section>

      {/* ---- the five categories -------------------------------------------
          What the platform covers, grouped the way a property business is run
          rather than the way the software is built. Everything listed is in
          the product today except the four marked, which are the roadmap. */}
      <section className="mk-section" id="categories">
        <div className="mk-center">
          <h2 className="mk-h2">What NyumbaSuite covers</h2>
        </div>
        <CoverageExplorer cats={CATEGORIES} />
      </section>

      {/* ---- the working screens -------------------------------------------
          Solutions listed every capability in words and showed none of them.
          The homepage carries the five screens a landlord judges the product
          on; these are the ones that answer "yes, but does it actually do X" —
          the document, the meter reading, the M-Pesa reconciliation — which is
          the question somebody reads THIS page to settle.

          Cropped to the content pane like every other screenshot on the site:
          the navigation is not in the files, so the shape of the product is not
          published to anybody who fetches the asset directly. */}
      <section className="mk-section" id="screens">
        <div className="mk-center">
          <h2 className="mk-h2">The screens behind the list</h2>
          <p className="mk-sub">
            Real screens from the running product, not mock-ups. Demo data: property names,
            tenant names and phone numbers are hidden, and yours appear as soon as you add a
            property.
          </p>
        </div>
        <div style={{ maxWidth: 'var(--container)', margin: '0 auto' }}>
          <CinematicReel slides={SOL_SCREENS} shape="screen" ms={4500} />
        </div>
      </section>

      {/* ---- property & unit management ------------------------------------ */}
      <section className="mk-section" id="property-management">
        <div className="sol-row">
          <div>
            <span className="mk-eyebrow">Property operations</span>
            <h3>Know every property. Every unit. Every detail.</h3>
            <p className="mk-p">Organise your entire portfolio in one place, and find any unit in seconds however large it grows.</p>
            <ul className="sol-caps">
              <li>Multiple properties</li><li>Units and unit details</li>
              <li>Occupancy status</li><li>Vacant units</li>
              <li>Search and filtering</li><li>Property documents</li>
            </ul>
            <Link className="btn btn-secondary btn-sm" to="/register">Manage your properties</Link>
          </div>
          <div className="sol-media">
            <PropertyRings props={[
              { name: 'Apartment block', units: 4, occupied: 3 },
              { name: 'Townhouse villas', units: 2, occupied: 1 },
              { name: 'Retail plaza', units: 2, occupied: 2 },
            ]} />
          </div>
        </div>
      </section>

      <div className="mk-band-wrap">
        <CinematicBand src="/cinematic/movein.jpg" alt="A couple receiving their keys at the door of a new apartment"
          kicker="Tenants" line="From the keys to the last receipt, on one record." chips={['Tenancy', 'Lease', 'Deposit', 'Portal']} />
      </div>

      {/* ---- tenant & tenancy --------------------------------------------- */}
      <section className="mk-section" id="tenant-management">
        <div className="sol-row rev">
          <div>
            <span className="mk-eyebrow">Property operations</span>
            <h3>Keep your tenants and tenancies organized</h3>
            <p className="mk-p">Every tenant, the tenancy that ties them to a unit, and the history behind both, in one record. Tenant history stays available even after a tenant moves out.</p>
            <ul className="sol-caps">
              <li>Tenant profiles</li><li>Contact information</li>
              <li>Tenancy dates and rent</li><li>Tenancy expiry</li>
              <li>Tenant history</li><li>Tenant communication</li>
            </ul>
          </div>
          <div className="sol-media">
            <div className="sol-mock">
              <div className="sol-mock-head"><span className="sol-mock-title">Daniel Otieno</span><span className="pill pill-success">Active</span></div>
              <div className="sol-kv"><span className="k">Unit</span><span className="v">V1</span></div>
              <div className="sol-kv"><span className="k">Monthly rent</span><span className="v">KES 140,000</span></div>
              <div className="sol-kv"><span className="k">Balance</span><span className="v">KES 0</span></div>
              <div className="sol-kv"><span className="k">Tenant since</span><span className="v">May 2026</span></div>
            </div>
          </div>
        </div>
      </section>

      <div className="mk-band-wrap">
        <CinematicBand src="/cinematic/landlord.jpg" alt="A landlord checking rent on his phone from his balcony"
          kicker="Rent" line="Know who has paid before you ask." chips={['Expected', 'Collected', 'Outstanding']} />
      </div>

      {/* ---- rent & payments ---------------------------------------------- */}
      <section className="mk-section" id="rent-payments">
        <div className="mk-center">
          <span className="mk-eyebrow">Financial operations</span>
          <h2 className="mk-h2">Know who has paid. Who hasn't. And what's outstanding.</h2>
          <p className="mk-lede">Record a payment and everything downstream follows automatically.</p>
        </div>
        <div className="sol-row" style={{ marginTop: 'var(--space-6)' }}>
          <div>
            <ul className="sol-caps">
              <li>Expected, collected, outstanding</li><li>Full and partial payments</li>
              <li>Advance payments and credit</li><li>Receipts, numbered</li>
              <li>Tenant statements</li><li>Arrears and aging</li>
            </ul>
            <p className="field-hint">Record M-Pesa and bank payments by reference today. Automated payment collection is planned, not yet live.</p>
          </div>
          <div className="sol-media"><PaymentDemo /></div>
        </div>
      </section>

      <div className="mk-band-wrap">
        <CinematicBand src="/cinematic/owner.jpg" alt="An owner and her accountant reviewing statements together"
          kicker="Owners" line="Statements an owner and an accountant can both trust." chips={['Income', 'Expenses', 'Net']} />
      </div>

      {/* ---- portfolio at a glance ---------------------------------------- */}
      <section className="mk-section tint" style={{ background: 'var(--brand-50)', maxWidth: 'none' }}>
        <div style={{ maxWidth: 'var(--container)', margin: '0 auto' }}>
          <div className="mk-center">
            <span className="mk-eyebrow">Portfolio intelligence</span>
            <h2 className="mk-h2">Know your property business at a glance</h2>
            <p className="mk-lede">NyumbaSuite turns raw property data into the numbers you actually manage by: occupancy, what is due, what has come in, and net income.</p>
          </div>
          <div style={{ maxWidth: 960, margin: 'var(--space-6) auto 0' }}><DashboardMock /></div>
        </div>
      </section>

      {/* ---- arrears ------------------------------------------------------- */}
      <section className="mk-section">
        <div className="sol-row">
          <div>
            <span className="mk-eyebrow">Financial operations</span>
            <h3>Stay ahead of rent arrears</h3>
            <p className="mk-p">See who is behind, by how much, and for how long, grouped into aging bands, so a 7-day gap gets a nudge before it grows into a 60-day write-off, and you chase the right tenant first.</p>
            <ul className="sol-caps">
              <li>Outstanding balances</li><li>Days overdue</li>
              <li>Partial payments</li><li>Property-level arrears</li>
            </ul>
            <Link className="btn btn-secondary btn-sm" to="/register">View rent performance</Link>
          </div>
          <div className="sol-media">
            <ArrearsBands />
          </div>
        </div>
      </section>

      {/* ---- maintenance -------------------------------------------------- */}
      <section className="mk-section" id="maintenance-operations">
        <div className="mk-center">
          <span className="mk-eyebrow">Property operations</span>
          <h2 className="mk-h2">Keep your properties running smoothly</h2>
        </div>
        <Photo className="sx-band" src="/solutions/repair.jpg"
          alt="A plumber fixing a kitchen tap while the property manager checks the job on a tablet"
          caption="From the tenant's message to the plumber's invoice, in one record." />
        <div className="sol-row rev" style={{ marginTop: 'var(--space-6)' }}>
          <div>
            <ul className="sol-caps">
              <li>Maintenance requests</li><li>Priority</li>
              <li>Property and unit</li><li>Assigned staff</li>
              <li>Estimated and actual cost</li><li>Maintenance history</li>
            </ul>
          </div>
          <div className="sol-media"><MaintenanceBoard /></div>
        </div>
      </section>

      {/* ---- expenses ----------------------------------------------------- */}
      <section className="mk-section" id="expenses">
        <div className="sol-row">
          <div>
            <span className="mk-eyebrow">Financial operations</span>
            <h3>Understand where your property money goes</h3>
            <p className="mk-p">Record expenses against the right property and see them against income, so profitability is never a guess.</p>
            <ul className="sol-caps">
              <li>Repairs and maintenance</li><li>Utilities</li>
              <li>Cleaning and security</li><li>Insurance and other</li>
            </ul>
          </div>
          <div className="sol-media">
            <MoneyChart kind="pair" />
          </div>
        </div>
      </section>

      {/* ---- communication ------------------------------------------------ */}
      <section className="mk-section tint" id="communication" style={{ background: 'var(--brand-50)', maxWidth: 'none' }}>
        <div style={{ maxWidth: 'var(--container)', margin: '0 auto' }}>
          <div className="mk-center">
            <span className="mk-eyebrow">Tenant experience</span>
            <h2 className="mk-h2">Keep tenants informed automatically</h2>
            <p className="mk-lede">Payment confirmations, rent reminders, overdue notices and tenancy-expiry reminders, sent for you.</p>
          </div>
          <StepFlow steps={[
            { label: 'Event', text: 'A payment is recorded, rent falls due or a lease nears its end.' },
            { label: 'Notification', text: 'The matching message is prepared for the tenant.' },
            { label: 'SMS / Email', text: 'Sent by SMS or email. WhatsApp is planned, not yet live.' },
            { label: 'Delivery status', text: 'Each message is logged as delivered or failed, so nothing is assumed.' },
          ]} />
          <p className="field-hint" style={{ textAlign: 'center', marginTop: 'var(--space-3)' }}>SMS and email today. WhatsApp is planned, not yet live.</p>
        </div>
      </section>

      {/* ---- analytics ---------------------------------------------------- */}
      <section className="mk-section" id="analytics">
        <div className="sol-row rev">
          <div>
            <span className="mk-eyebrow">Portfolio intelligence</span>
            <h3>See where your portfolio is heading, not just where it's been</h3>
            <p className="mk-p">Occupancy, collection and income trends surface the pattern early, whether that is arrears creeping up or a unit sitting vacant too long, instead of waiting for a report to tell you afterwards. Downloadable reports are still there for the paper trail.</p>
            <ul className="sol-caps">
              <li>Occupancy and vacancy rate</li><li>Rent collection rate</li>
              <li>Revenue and expenses</li><li>Property performance</li>
            </ul>
            <p className="field-hint">Trends and early signals drawn from your own records: a clear read of what's already moving, not a black-box forecast.</p>
          </div>
          <div className="sol-media">
            <MoneyChart kind="line" />
          </div>
        </div>
      </section>

      {/* ---- documents ---------------------------------------------------- */}
      <section className="mk-section narrow" style={{ textAlign: 'center' }}>
        <span className="mk-eyebrow">Property operations</span>
        <h2 className="mk-h2">Keep your property documents in one place</h2>
        <p className="mk-p" style={{ marginLeft: 'auto', marginRight: 'auto', maxWidth: '60ch' }}>Associate agreements, ID copies and receipts with the record they belong to.</p>
        <div className="sol-eco" style={{ marginTop: 'var(--space-5)' }}>
          {['Properties', 'Units', 'Tenants', 'Tenancies', 'Expenses', 'Maintenance'].map((t) => <span key={t} className="sol-eco-tag">{t}</span>)}
        </div>
      </section>

      {/* ---- tenant portal -------------------------------------------------
          Anchored, because SOLUTIONS in lib/solutions.ts now carries an entry
          for it and the header dropdown links to /solutions#tenant-portal. A
          dropdown item pointing at a section that does not exist scrolls
          nowhere and looks broken, so the two are added together. */}
      <section className="mk-section" id="tenant-portal">
        <div className="sol-row">
          <div>
            <span className="mk-eyebrow">Tenant experience</span>
            <h3>Give every tenant a login of their own</h3>
            <p className="mk-p">Most of the calls a landlord takes are questions a tenant could answer themselves: what do I owe, did my payment land, can I have a statement. Invite a tenant and they can see all of it, and raise a repair without ringing anyone.</p>
            <ul className="sol-caps">
              <li>Balance and rent charges</li><li>Invoices, receipts and statements</li>
              <li>Lease, tenancy and documents</li><li>Maintenance requests they can follow</li>
            </ul>
            <p className="field-hint">Access is by invitation from you, tied to the tenancy you already hold. There is no public tenant sign-up.</p>
          </div>
          <div className="sol-media">
            <div className="sx-portal">
              <Photo src="/solutions/tenant.jpg" alt="A tenant relaxing at home with her phone" />
              <PortalPhone />
            </div>
          </div>
        </div>
      </section>

      {/* ---- leases and inspections ---------------------------------------- */}
      <section className="mk-section tint" style={{ background: 'var(--brand-50)', maxWidth: 'none' }}>
        <div style={{ maxWidth: 'var(--container)', margin: '0 auto' }} id="leases-inspections">
          <div className="mk-center">
            <span className="mk-eyebrow">Property operations</span>
            <h2 className="mk-h2">The paperwork that settles the argument later</h2>
            <p className="mk-lede">Two things decide most disputes between a landlord and a tenant: what the lease said, and what the unit looked like on the day they moved in. Both are recorded here rather than remembered.</p>
          </div>
          <div className="mk-grid sx-two">
            <article className="mk-card">
              <div className="mk-card-icon ic-rose" aria-hidden>✎</div>
              <h3 className="mk-card-name">Lease agreements</h3>
              <p className="mk-card-desc">A lease document with a life of its own, separate from the tenancy dates: drafted, activated, renewed or ended, with every renewal kept on the record.</p>
              <StepFlow steps={[
                { label: 'Draft', text: 'Prepared from the tenancy you already hold.' },
                { label: 'Active', text: 'In force, with dates of its own, separate from the tenancy.' },
                { label: 'Expiring', text: 'Reminders go out at 90, 60 and 30 days.' },
                { label: 'Renewed', text: 'The renewal is kept on the record beside the original.' },
              ]} />
              <p className="field-hint">Reminders at 90, 60 and 30 days, so a renewal is never a surprise.</p>
            </article>
            <article className="mk-card">
              <div className="mk-card-icon ic-teal" aria-hidden>☑</div>
              <h3 className="mk-card-name">Move-in and move-out inspections</h3>
              <p className="mk-card-desc">Walk the unit room by room with photos and meter readings, and have the tenant sign it from their own phone. Move-out is compared against move-in, side by side.</p>
              <StepFlow steps={[
                { label: 'Inspect', text: 'Walk the unit room by room.' },
                { label: 'Photos', text: 'Photos and meter readings attach to each room.' },
                { label: 'Tenant signs', text: 'Signed by the tenant from their own phone.' },
                { label: 'Compare', text: 'Move-out is set against move-in, side by side.' },
                { label: 'Deposit', text: 'A deposit worksheet built from the differences.' },
              ]} />
              <p className="field-hint">The deposit worksheet is built from the differences, not from an argument.</p>
            </article>
          </div>
        </div>
      </section>

      {/* ---- the assistant -------------------------------------------------- */}
      <section className="mk-section" id="ai-assistant">
        <div className="sol-row rev">
          <div>
            <span className="mk-eyebrow">AI &amp; automation</span>
            <h3>Ask your portfolio a question</h3>
            <p className="mk-p">Type the question you would have asked your accountant and get the answer from your own records, not a help article and not a guess. Your tenants get an assistant of their own inside their portal.</p>
            <ul className="sol-caps">
              <li>Who is in arrears, and by how much</li><li>Which units are vacant</li>
              <li>Which leases expire soon</li><li>How collection is trending</li>
            </ul>
            <p className="field-hint">It answers within the permissions your own role already has, and it reads rather than writes. It can explain your portfolio, never change it.</p>
          </div>
          <div className="sol-media">
            <AskDemo />
          </div>
        </div>
      </section>

      {/* ---- marketplace ----------------------------------------------------
          This section said "Coming soon" on both cards for months after the
          marketplace shipped — while the site header linked to /homes and the
          landlord could publish a listing from any vacant unit. Marking a built
          feature as unbuilt is the one marketing error that costs a sale twice:
          the visitor does not try it, and does not believe the next claim. */}
      <section className="mk-section" id="listing-search">
        <div className="mk-center">
          <span className="mk-eyebrow">Connect</span>
          <h2 className="mk-h2">A property ecosystem, not just management</h2>
          <p className="mk-lede">A vacant unit earns nothing. Advertise it on the public marketplace straight from your portfolio, and let people looking for a home find it.</p>
        </div>
        <Photo className="sx-band" src="/solutions/vacant.jpg" alt="A bright empty apartment with house keys on the counter, ready to list" caption="A vacant unit earns nothing. Publish it from the unit you already hold." />
        <div className="mk-grid sx-two" style={{ marginTop: 'var(--space-6)' }}>
          <article className="mk-card">
            <div className="mk-card-icon ic-violet" aria-hidden>⌕</div>
            <h3 className="mk-card-name">Find your next home</h3>
            <p className="mk-card-desc">Discover available properties by location, type, bedrooms, rent range and availability, with photos and full details.</p>
            <div style={{ marginTop: 'var(--space-4)' }}><Link className="btn btn-secondary" to="/homes">Browse homes</Link></div>
          </article>
          <article className="mk-card">
            <div className="mk-card-icon ic-amber" aria-hidden>⌂</div>
            <h3 className="mk-card-name">List your property</h3>
            <p className="mk-card-desc">Advertise a vacant unit straight from your portfolio, with no duplicate data entry, and receive enquiries in NyumbaSuite.</p>
            <StepFlow steps={[
              { label: 'Add property', text: 'Already done: the unit is in your portfolio.' },
              { label: 'Photos', text: 'Add photos of the unit.' },
              { label: 'Set rent', text: 'Set the asking rent.' },
              { label: 'Publish', text: 'Live on the public marketplace in a couple of clicks.' },
              { label: 'Enquiries', text: 'Enquiries land back in your NyumbaSuite inbox.' },
            ]} />
          </article>
        </div>
      </section>

      {/* ---- built for every stage ---------------------------------------- */}
      <section className="mk-section">
        <div className="mk-center">
          <span className="mk-eyebrow">Grow</span>
          <h2 className="mk-h2">Whether you own 3 units or 100+, NyumbaSuite grows with you</h2>
        </div>
        <StageSlider stages={[
          { range: '1-10', title: 'Getting started', text: 'For landlords managing a few units who want to leave spreadsheets behind.' },
          { range: '11-50', title: 'Growing portfolio', text: 'For landlords adding properties, units and tenants as they expand.' },
          { range: '51+', title: 'Professional management', text: 'For larger landlords and property managers running multiple properties.' },
        ]} />
        <p className="page-sub" style={{ textAlign: 'center', marginTop: 'var(--space-5)' }}>
          Plans scale with your portfolio. <Link to="/#pricing">See pricing →</Link>
        </p>
      </section>

      {/* ---- before / after ----------------------------------------------- */}
      <section className="mk-section tint" style={{ background: 'var(--brand-50)', maxWidth: 'none' }}>
        <div style={{ maxWidth: 'var(--container)', margin: '0 auto' }}>
          <div className="mk-center">
            <span className="mk-eyebrow">Grow</span>
            <h2 className="mk-h2">Still managing with Excel, WhatsApp and notebooks?</h2>
          </div>
          <BeforeAfter pairs={[
            ['Manual rent tracking', 'Centralized property records'],
            ['Scattered tenant information', 'Organized tenant information'],
            ['Lost payment records', 'Full payment history'],
            ['Forgotten follow-ups', 'Arrears visibility'],
            ['Difficult reporting', 'Portfolio analytics'],
            ['Limited visibility', 'Automated notifications'],
          ]} />
        </div>
      </section>

      {/* ---- ecosystem summary -------------------------------------------- */}
      <section className="mk-section narrow" style={{ textAlign: 'center' }}>
        <h2 className="mk-h2">One platform. Your entire property ecosystem.</h2>
        <div className="sol-eco">
          {([['Property management', 'property-management'], ['Tenant management', 'tenant-management'], ['Rent & payments', 'rent-payments'],
            ['Maintenance', 'maintenance-operations'], ['Expenses', 'expenses'], ['Communication', 'communication'], ['Analytics', 'analytics'],
            ['Property discovery', 'listing-search'], ['Property listings', 'listing-search']] as const).map(([t, id]) => (
            <a key={t} href={`#${id}`} className="sol-eco-tag sx-eco-link">{t}</a>
          ))}
        </div>
      </section>

      {/* ---- final CTA ---------------------------------------------------- */}
      <section className="mk-cta">
        <h2>Ready to take control of your property portfolio?</h2>
        <p>Start with what you need today and grow with NyumbaSuite as your portfolio grows.</p>
        <div className="mk-cta-row">
          <Link className="btn btn-invert" to="/register">Get started</Link>
          <Link className="btn btn-outline-invert" to="/demo">Request a demo</Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
